import http from './client';
import { errorHandler } from './errorHandler';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '../config/api';
import {
  bumpAuthWriteGeneration,
  getAuthWriteGeneration,
  isStaleAuthWrite,
} from './authSessionGuard';
import type {
  ApiResponse,
  User,
  UserProfileRow,
  ImpersonationState,
} from './types';

interface LoginData {
  email: string;
  password: string;
}

interface VerifyEmailData {
  emailornumber: string;
  otp: string;
  is_mobile: boolean;
  firebaseToken?: string;
}

interface RegisterDoctorStep1Data {
  // Legacy flow can send combined name or split parts
  name?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  email: string;
  password: string;
  confirm_password: string;
  number?: string;
  country_code?: string;
}

interface VerifyDoctorEmailData {
  user_id: number;
  otp: string;
}

interface ResumeDoctorUser {
  id?: number | string;
  name?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  email?: string;
  number?: string;
  country_code?: string;
}

interface ResumeDoctorProfile {
  specialization?: string;
  area_of_expertise?: string;
  qualifications?: string;
  license_number?: string;
  medical_council_registration?: string;
  professional_bio?: string;
  certificate_expiry?: string;
  languages_spoken?: string | string[];
  availability?: Record<string, unknown>;
}

interface GoogleLoginData {
  id_token: string;
}

interface VerifyPhoneData {
  firebaseToken: string;
}

interface AuthResponseData {
  message?: string;
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  user_id?: number;
  email_verification_required?: boolean;
  phone_verification_required?: boolean;
  basic_profile_required?: boolean; // Doctor: needs to complete basic profile
  otp?: string;
  is_mobile?: boolean;
  resume_registration?: boolean; // Flag to indicate resume registration
  redirect_to_otp?: boolean; // Flag to redirect to OTP screen (Scenario 1 & 3)
  validation?: Record<string, string | string[]>; // Field-level errors from API
  impersonation?: ImpersonationState;
}

interface StartImpersonationData {
  target_user_id: number | string;
  support_request_id?: number | string;
  reason?: string;
  ticket_id?: string;
  scope?: 'read_only' | 'support_write';
}

interface ImpersonationSupportRequestData {
  issue_message: string;
  ticket_id?: string;
}

interface ImpersonationSupportRequestItem {
  id: number | string;
  requester_user_id: number | string;
  issue_message: string;
  ticket_id?: string | null;
  status: 'open' | 'in_progress' | 'resolved';
  consent_status?: 'not_requested' | 'pending' | 'approved' | 'denied';
  consent_requested_at?: string | null;
  consent_responded_at?: string | null;
  consent_used_at?: string | null;
  handled_at?: string | null;
  created_at?: string;
  requester?: {
    id?: number | string;
    name?: string;
    email?: string;
    role?: string;
  };
  handled_by_admin?: {
    id?: number | string;
    name?: string;
    email?: string;
  } | null;
}

export const persistAuthData = (data: AuthResponseData) => {
  try {
    if (data.token || data.accessToken) {
      const token = data.token || data.accessToken;
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      }
    }
    if (data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }
    if (data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    }
  } catch (error) {
    console.error('Failed to store auth data:', error);
    throw new Error(
      'Failed to save login information. Please check your browser settings.',
    );
  }
};

/** Persist only if the auth write generation still matches (guards against stale refresh). */
export const persistAuthDataIfCurrent = (
  data: AuthResponseData,
  generationAtStart: number,
) => {
  if (isStaleAuthWrite(generationAtStart)) return false;
  persistAuthData(data);
  return true;
};

export const authApi = {
  // Backward-compatible doctor step 1 registration API
  registerDoctorStep1: async (
    data: RegisterDoctorStep1Data,
    _legacyOptions?: unknown,
  ): Promise<
    ApiResponse<
      AuthResponseData & { validation?: Record<string, string | string[]> }
    >
  > => {
    try {
      const response = await http.post('/auth/register-doctor-step1', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? { validation: err.response.data.validation }
          : undefined,
      };
    }
  },

  // Backward-compatible doctor step 1 update API
  updateDoctorStep1: async (
    data: RegisterDoctorStep1Data & { user_id: number },
    _legacyOptions?: unknown,
  ): Promise<
    ApiResponse<
      AuthResponseData & {
        email_changed?: boolean;
        validation?: Record<string, string | string[]>;
      }
    >
  > => {
    try {
      const response = await http.put('/auth/update-doctor-step1', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? { validation: err.response.data.validation }
          : undefined,
      };
    }
  },

  // Backward-compatible doctor email verification API
  verifyDoctorEmail: async (
    data: VerifyDoctorEmailData,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/verify-doctor-email', data);
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? { validation: err.response.data.validation }
          : undefined,
      };
    }
  },

  // Backward-compatible doctor email check API (resume registration)
  checkDoctorEmail: async (
    email: string,
    _legacyOptions?: unknown,
  ): Promise<
    ApiResponse<{
      can_resume?: boolean;
      user?: ResumeDoctorUser;
      doctorProfile?: ResumeDoctorProfile;
      message?: string;
    }>
  > => {
    try {
      const response = await http.get(
        `/auth/check-doctor-email/${encodeURIComponent(email)}`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Backward-compatible resend doctor OTP API
  resendDoctorOTP: async (
    userId: number,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/resend-doctor-otp', {
        user_id: userId,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch {
      // Fallback for deployments still using generic resend endpoint
      try {
        const response = await http.post('/auth/resend-otp', {
          user_id: userId,
        });
        return {
          success: true,
          data: response.data,
        };
      } catch (error) {
        return {
          success: false,
          error: errorHandler(error),
        };
      }
    }
  },

  //verify email user - API
  verifyEmail: async (
    data: VerifyEmailData,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/verify-email', data);
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? { validation: err.response.data.validation }
          : undefined,
      };
    }
  },
  //resend user otp - API
  resendOTP: async (email: string): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/resend-otp', { email });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: { validation?: Record<string, string | string[]> };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? { validation: err.response.data.validation }
          : undefined,
      };
    }
  },
  //Login  API
  login: async (
    credentials: LoginData,
  ): Promise<
    ApiResponse<
      AuthResponseData & { validation?: Record<string, string | string[]> }
    >
  > => {
    try {
      const response = await http.post('/auth/login', credentials);
      const data = response.data;
      persistAuthData(data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      const err = error as {
        response?: {
          data?: {
            validation?: Record<string, string | string[]>;
            message?: string;
          };
        };
      };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data
          ? {
              validation: err.response.data.validation,
              message: err.response.data.message,
            }
          : undefined,
      };
    }
  },

  forgotPassword: async (
    email: string,
  ): Promise<
    ApiResponse<{
      message: string;
      resetLink?: string;
      token?: string;
      emailRegistered?: boolean;
    }>
  > => {
    try {
      const response = await http.post('/auth/forgot-password', { email });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // Check if error response contains emailRegistered flag
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { emailRegistered?: boolean; error?: string } };
        };
        if (axiosError.response?.data?.emailRegistered === false) {
          const errorMessage =
            axiosError.response.data.error || errorHandler(error);
          return {
            success: false,
            error: errorMessage,
            data: {
              message: errorMessage,
              emailRegistered: false,
            },
          };
        }
      }
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  verifyResetToken: async (data: {
    email: string;
    token: string;
  }): Promise<ApiResponse<{ valid: boolean; message: string }>> => {
    try {
      const response = await http.get('/auth/verify-reset-token', {
        params: data,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  resetPassword: async (data: {
    email: string;
    token: string;
    new_password: string;
    confirm_password: string;
  }): Promise<ApiResponse<{ message: string }>> => {
    try {
      const response = await http.post('/auth/reset-password', data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  claimDependentAccount: async (data: {
    token: string;
    email: string;
    password: string;
    confirm_password: string;
  }): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/claim-account', data);
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Google login - API
  googleLogin: async (
    data: GoogleLoginData,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/google-login', data);
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Send phone OTP - API
  sendPhoneOTP: async (
    userId?: number,
  ): Promise<ApiResponse<{ message: string; otp?: string }>> => {
    try {
      const response = await http.post('/auth/send-phone-otp', {
        user_id: userId,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Refresh Token - API
  refreshToken: async (
    token: string,
  ): Promise<ApiResponse<AuthResponseData>> => {
    // Note: check client.ts for the actual interceptor implementation that uses axios directly
    // This method is exposed here for manual usage if needed
    const generationAtStart = getAuthWriteGeneration();
    try {
      const response = await http.post('/auth/refresh-token', {
        refreshToken: token,
      });
      const data = response.data;
      if (!persistAuthDataIfCurrent(data, generationAtStart)) {
        return {
          success: false,
          error: 'Stale auth session',
        };
      }
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Verify phone - API
  verifyPhone: async (
    data: VerifyPhoneData,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/auth/verify-phone', data);
      const responseData = response.data;

      // Store token and user data in localStorage
      persistAuthData(responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
  //for logging out
  logout: (): void => {
    console.log('🚪 [LOGOUT] authApi.logout() called - clearing all auth data');
    console.trace('🚪 [LOGOUT] Stack trace:'); // This will show where logout was called from
    // Invalidate in-flight refresh / persist so a previous session cannot clobber the next login.
    bumpAuthWriteGeneration();
    // Clear authentication data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    // Clear user-related data (saved recipes, liked recipes, collections)
    localStorage.removeItem('liked_recipes');
    localStorage.removeItem('saved_recipes');
    localStorage.removeItem('recipe_collections');
    console.log('🚪 [LOGOUT] All user data cleared from localStorage');
  },

  // Signup Flow - API
  signupInit: async (data: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    phone_number: string;
    country_code: string;
    role: string;
    user_id?: number;
    invite_token?: string;
  }): Promise<ApiResponse<AuthResponseData & { user_exists?: boolean }>> => {
    try {
      const response = await http.post('/auth/signup-init', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  signupVerifyPhone: async (data: {
    firebase_token: string;
    country_code?: string;
    phone_number?: string;
  }): Promise<ApiResponse<AuthResponseData & { is_new_user?: boolean }>> => {
    try {
      const response = await http.post('/auth/signup-verify-phone', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: errorHandler(error) };
    }
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  getUser: (): User | null => {
    try {
      const userStr = localStorage.getItem(USER_KEY);
      if (!userStr) return null;
      return JSON.parse(userStr) as User;
    } catch (error) {
      console.error('Failed to parse user data:', error);
      // Clear corrupted data
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },

  switchProfile: async (
    targetProfileType: 'patient' | 'doctor',
  ): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> => {
    try {
      const response = await http.post('/auth/switch-profile', {
        target_profile_type: targetProfileType,
      });
      return {
        success: true,
        data: {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  addDoctorProfile: async (): Promise<
    ApiResponse<{ doctorProfile?: unknown; message?: string }>
  > => {
    try {
      const response = await http.post('/auth/add-doctor-profile');
      return { success: true, data: response.data };
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      return {
        success: false,
        error: errorHandler(error),
        data: err.response?.data,
      };
    }
  },

  getMyProfiles: async (): Promise<
    ApiResponse<{ profiles: UserProfileRow[] }>
  > => {
    try {
      const response = await http.get('/auth/my-profiles');
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  startImpersonation: async (
    data: StartImpersonationData,
  ): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/admin/impersonation/start', data);
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  stopImpersonation: async (): Promise<ApiResponse<AuthResponseData>> => {
    try {
      const response = await http.post('/admin/impersonation/stop');
      const responseData = response.data;
      persistAuthData(responseData);
      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  createImpersonationSupportRequest: async (
    data: ImpersonationSupportRequestData,
  ): Promise<ApiResponse<{ id: number | string }>> => {
    try {
      const response = await http.post(
        '/users/support/impersonation-request',
        data,
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getMyImpersonationSupportRequests: async (
    status: 'all' | 'open' | 'in_progress' | 'resolved' = 'all',
    page = 1,
    limit = 20,
  ): Promise<
    ApiResponse<{
      data: ImpersonationSupportRequestItem[];
      pagination?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>
  > => {
    try {
      const response = await http.get('/users/support/impersonation-requests', {
        params: { status, page, limit },
      });
      return {
        success: true,
        data: {
          data: response.data?.data || [],
          pagination: response.data?.pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getImpersonationSupportRequests: async (
    status: 'all' | 'open' | 'in_progress' | 'resolved' = 'open',
    consentStatus?: 'not_requested' | 'pending' | 'approved' | 'denied',
    page = 1,
    limit = 20,
  ): Promise<
    ApiResponse<{
      data: ImpersonationSupportRequestItem[];
      pagination?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>
  > => {
    try {
      const response = await http.get('/admin/impersonation-support-requests', {
        params: { status, consent_status: consentStatus, page, limit },
      });
      return {
        success: true,
        data: {
          data: response.data?.data || [],
          pagination: response.data?.pagination,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  updateImpersonationSupportRequestStatus: async (
    id: number | string,
    status: 'open' | 'in_progress' | 'resolved',
  ): Promise<ApiResponse<{ status: string }>> => {
    try {
      const response = await http.patch(
        `/admin/impersonation-support-requests/${id}/status`,
        { status },
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  requestImpersonationConsent: async (
    id: number | string,
  ): Promise<ApiResponse<{ consent_status: string }>> => {
    try {
      const response = await http.post(
        `/admin/impersonation-support-requests/${id}/request-consent`,
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  getMyPendingImpersonationConsentRequests: async (): Promise<
    ApiResponse<{ data: ImpersonationSupportRequestItem[] }>
  > => {
    try {
      const response = await http.get(
        '/users/support/impersonation-consent-requests',
      );
      return {
        success: true,
        data: {
          data: response.data?.data || [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  respondImpersonationConsentRequest: async (
    id: number | string,
    action: 'approve' | 'deny',
  ): Promise<ApiResponse<{ consent_status: string }>> => {
    try {
      const response = await http.post(
        `/users/support/impersonation-consent-requests/${id}/respond`,
        { action },
      );
      return {
        success: true,
        data: response.data?.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },

  // Check if phone number is registered
  checkPhone: async (
    phone: string,
  ): Promise<ApiResponse<{ exists: boolean; message: string }>> => {
    try {
      const response = await http.get(`/auth/check-phone/${phone}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: errorHandler(error),
      };
    }
  },
};
